/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useMemo, useState, useId } from 'react';
import { AlertTriangle, CheckCircle, Clipboard, Download, Plus, Trash2 } from 'lucide-react';
import { buildGradientDefinition, GradientBuildResult, GradientSpace, GradientStopInput } from '../lib/gradientBuilder';
import { exportFormats } from '../lib/exportFormats';

interface GradientBuilderPanelProps {
  paletteName: string;
  paletteColors: string[];
  onApplyToPalette: (colors: string[]) => Promise<void>;
}

interface GradientStopDraft extends GradientStopInput {
  id: string;
}

const clamp01 = (value: number) => {
  if (!Number.isFinite(value)) return 0;
  return Math.min(Math.max(value, 0), 1);
};

const toPercent = (fraction: number) => Math.round(clamp01(fraction) * 100);

const createStopId = () => `stop-${Math.random().toString(36).slice(2, 10)}`;

const buildDefaultStops = (paletteColors: string[]): GradientStopDraft[] => {
  if (paletteColors.length >= 2) {
    return [
      { id: createStopId(), color: paletteColors[0], position: 0 },
      { id: createStopId(), color: paletteColors[paletteColors.length - 1], position: 1 },
    ];
  }

  if (paletteColors.length === 1) {
    return [
      { id: createStopId(), color: paletteColors[0], position: 0 },
      { id: createStopId(), color: paletteColors[0], position: 1 },
    ];
  }

  return [
    { id: createStopId(), color: '#000000', position: 0 },
    { id: createStopId(), color: '#ffffff', position: 1 },
  ];
};

const filenameSafe = (name: string, extension: string) => {
  const safeName = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'gradient';
  return `${safeName}${extension}`;
};

export function GradientBuilderPanel({ paletteName, paletteColors, onApplyToPalette }: GradientBuilderPanelProps) {
  const gradientId = useId();
  const [stops, setStops] = useState<GradientStopDraft[]>(() => buildDefaultStops(paletteColors));
  const [space, setSpace] = useState<GradientSpace>('oklch');
  const [sampleCount, setSampleCount] = useState<number>(8);
  const [result, setResult] = useState<GradientBuildResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [applyStatus, setApplyStatus] = useState<'idle' | 'working' | 'success' | 'error'>('idle');

  const paletteColorsString = useMemo(() => paletteColors.map((color) => color).join('|'), [paletteColors]);
  
  useEffect(() => {
    setStops(buildDefaultStops(paletteColors));
  }, [paletteColorsString, paletteColors]);

  const orderedStops = useMemo(() => {
    return [...stops].sort((a, b) => {
      if (a.position === b.position) {
        return a.id.localeCompare(b.id);
      }
      return a.position - b.position;
    });
  }, [stops]);

  useEffect(() => {
    try {
      const build = buildGradientDefinition(orderedStops, {
        space,
        sampleCount,
        gradientId: gradientId.replace(/:/g, ''),
      });
      setResult(build);
      setError(null);
    } catch (err) {
      setResult(null);
      setError(err instanceof Error ? err.message : 'Failed to build gradient');
    }
  }, [orderedStops, space, sampleCount, gradientId]);

  useEffect(() => {
    if (copyStatus === 'idle') return;
    const timer = setTimeout(() => setCopyStatus('idle'), 2500);
    return () => clearTimeout(timer);
  }, [copyStatus]);

  useEffect(() => {
    if (applyStatus === 'idle' || applyStatus === 'working') return;
    const timer = setTimeout(() => setApplyStatus('idle'), 2500);
    return () => clearTimeout(timer);
  }, [applyStatus]);

  const handleStopColorChange = (id: string, color: string) => {
    setStops((prev) => prev.map((stop) => (stop.id === id ? { ...stop, color } : stop)));
  };

  const handleStopPositionChange = (id: string, positionPercent: number) => {
    const fraction = clamp01(positionPercent / 100);
    setStops((prev) => prev.map((stop) => (stop.id === id ? { ...stop, position: fraction } : stop)));
  };

  const handlePaletteSelection = (id: string, color: string) => {
    if (!color) return;
    setStops((prev) => prev.map((stop) => (stop.id === id ? { ...stop, color } : stop)));
  };

  const addStop = () => {
    const midpoint = 0.5;
    const fallbackColor = orderedStops.length ? orderedStops[Math.floor(orderedStops.length / 2)].color : '#777777';
    setStops((prev) => [...prev, { id: createStopId(), color: fallbackColor, position: midpoint }]);
  };

  const removeStop = (id: string) => {
    setStops((prev) => (prev.length <= 2 ? prev : prev.filter((stop) => stop.id !== id)));
  };

  const copyCssToClipboard = async () => {
    if (!result?.css || typeof navigator === 'undefined' || !navigator.clipboard) {
      setCopyStatus('error');
      return;
    }
    try {
      await navigator.clipboard.writeText(result.css);
      setCopyStatus('success');
    } catch (err) {
      console.error('Failed to copy gradient CSS:', err);
      setCopyStatus('error');
    }
  };

  const copySamplesToClipboard = async () => {
    if (!result?.samples?.length || typeof navigator === 'undefined' || !navigator.clipboard) {
      setCopyStatus('error');
      return;
    }
    try {
      await navigator.clipboard.writeText(result.samples.join(', '));
      setCopyStatus('success');
    } catch (err) {
      console.error('Failed to copy gradient samples:', err);
      setCopyStatus('error');
    }
  };

  const downloadCss = () => {
    if (!result) return;
    exportFormats.downloadFile(`background: ${result.css};\n`, filenameSafe(`${paletteName}-gradient`, '.css'), 'text/css');
  };

  const downloadSvg = () => {
    if (!result) return;
    exportFormats.downloadFile(result.svg, filenameSafe(`${paletteName}-gradient`, '.svg'), 'image/svg+xml');
  };

  const applyToPalette = async () => {
    if (!result?.samples?.length) return;
    setApplyStatus('working');
    try {
      await onApplyToPalette(result.samples);
      setApplyStatus('success');
    } catch (err) {
      console.error('Failed to apply gradient samples to palette:', err);
      setApplyStatus('error');
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="font-semibold text-gray-900 dark:text-gray-100">OKLCH / OKLab Gradient Builder</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400">Design continuous ramps, preview them live, then export CSS or SVG assets.</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <label className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
            <input
              type="radio"
              name={`gradient-space-${gradientId}`}
              value="oklch"
              checked={space === 'oklch'}
              onChange={() => setSpace('oklch')}
              className="w-4 h-4 text-blue-600"
            />
            OKLCH
          </label>
          <label className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
            <input
              type="radio"
              name={`gradient-space-${gradientId}`}
              value="oklab"
              checked={space === 'oklab'}
              onChange={() => setSpace('oklab')}
              className="w-4 h-4 text-blue-600"
            />
            OKLab
          </label>
        </div>
      </div>

      <div className="space-y-3">
        {orderedStops.map((stop, index) => (
          <div
            key={stop.id}
            className="border border-gray-200 dark:border-gray-700 rounded-md p-3 bg-white dark:bg-gray-800 space-y-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded border border-gray-200 dark:border-gray-600"
                  style={{ backgroundColor: stop.color }}
                  aria-label={`Gradient stop ${index + 1} color preview`}
                />
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <p className="font-semibold text-gray-800 dark:text-gray-100">Stop {index + 1}</p>
                  <p>{toPercent(stop.position)}%</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeStop(stop.id)}
                disabled={orderedStops.length <= 2}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-red-200 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-300 space-y-1">
                Hex / CSS Color
                <input
                  type="text"
                  value={stop.color}
                  onChange={(event) => handleStopColorChange(stop.id, event.target.value)}
                  placeholder="#ff7f50"
                  className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 font-mono"
                />
              </label>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-300 space-y-1">
                Position (%)
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={toPercent(stop.position)}
                  onChange={(event) => handleStopPositionChange(stop.id, Number(event.target.value))}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100"
                />
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={toPercent(stop.position)}
                  onChange={(event) => handleStopPositionChange(stop.id, Number(event.target.value))}
                  className="w-full accent-blue-600"
                />
              </label>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-300 space-y-1">
                Use Palette Color
                <select
                  value=""
                  onChange={(event) => handlePaletteSelection(stop.id, event.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100"
                >
                  <option value="">Select palette color</option>
                  {paletteColors.map((color, paletteIndex) => (
                    <option key={`${color}-${paletteIndex}`} value={color}>
                      {color}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={addStop}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <Plus className="w-4 h-4" /> Add stop
        </button>
        <label className="text-xs font-medium text-gray-600 dark:text-gray-300 flex items-center gap-2">
          Samples for ramp application
          <input
            type="number"
            min={2}
            max={24}
            value={sampleCount}
            onChange={(event) => setSampleCount(Number(event.target.value))}
            className="w-16 px-2 py-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100"
          />
        </label>
      </div>

      <div className="space-y-2">
        <div
          className="w-full h-24 rounded-lg border border-gray-200 dark:border-gray-700"
          style={{ backgroundImage: result?.css || 'linear-gradient(90deg, #000000, #ffffff)' }}
          aria-label="Gradient preview"
        />
        <p className="text-[11px] text-gray-500 dark:text-gray-400">Preview updates automatically as you tune stops. CSS output is normalized by stop position.</p>
      </div>

      {error ? (
        <div className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/40 rounded-md p-3 bg-red-50 dark:bg-red-900/20">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      ) : null}

      {result ? (
        <div className="space-y-3">
          <div className="font-mono text-[11px] bg-gray-900 text-gray-100 rounded-md p-3 overflow-x-auto">
            <code>{result.css}</code>
          </div>
          <div className="font-mono text-[11px] bg-gray-900 text-gray-100 rounded-md p-3 overflow-x-auto">
            <code>{result.samples.join(', ')}</code>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={copyCssToClipboard}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Clipboard className="w-4 h-4" /> Copy CSS
            </button>
            <button
              type="button"
              onClick={copySamplesToClipboard}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Clipboard className="w-4 h-4" /> Copy HEX ramp
            </button>
            <button
              type="button"
              onClick={downloadCss}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Download className="w-4 h-4" /> Download CSS
            </button>
            <button
              type="button"
              onClick={downloadSvg}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Download className="w-4 h-4" /> Download SVG
            </button>
            <button
              type="button"
              onClick={applyToPalette}
              disabled={applyStatus === 'working'}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-emerald-400"
            >
              {applyStatus === 'working' ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Apply ramp to palette
            </button>
          </div>
          {copyStatus === 'success' && (
            <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-300">
              <CheckCircle className="w-3 h-3" /> Copied to clipboard
            </div>
          )}
          {copyStatus === 'error' && (
            <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
              <AlertTriangle className="w-3 h-3" /> Clipboard access unavailable
            </div>
          )}
          {applyStatus === 'success' && (
            <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-300">
              <CheckCircle className="w-3 h-3" /> Gradient applied to palette
            </div>
          )}
          {applyStatus === 'error' && (
            <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
              <AlertTriangle className="w-3 h-3" /> Failed to apply ramp. Try again.
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
